import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ManageTitle from "../../../../Components/TitleElements/ManageTitle";
import HeaderOrg from "../../../Headers/HeaderOrg";
import Help from "../../../../Components/Help/Help";
import Loader from "../../../../Components/Loader/Loader";
import { serviceConsumer } from "../../../../network/ServiceConsumer";
import OneJudgeResult from "./OneJudgeResult";
import ThreeJudgeResult from "./ThreeJudgeResult";
import NineJudgeResult from "./NineJudgeResult";
import { ToastContainer } from "react-toastify";
import { ShowToastMessage } from "../../../../helper/ShowToastMessage";
import { getPrintHandler } from "../../../../helper/printHandlers";
import { Button } from "react-bootstrap";
import RoundMedalsModal from "../../../../Components/Modals/OrganizerModel/RoundMedalModel";
import RoundMedalsTable from "./RoundMedalsTable";
import { useCan } from "../../../../device/DeviceContext";

const CompleteResults = () => {
  const [event, setEvent] = useState();
  const [results, setResults] = useState([]);
  const [competitionCode, setCompetitionCode] = useState("");
  const [competitionId, setCompetitionId] = useState("");
  const [loading, setLoading] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [adjudicatorCount, setAdjudicatorCount] = useState(0);
  const [rounds, setRounds] = useState(0);
  const [buttonEnabled, setButtonEnabled] = useState(false);
  const [tableDataKey, setTableDataKey] = useState(0);
  const [initialLoading, setInitialLoading] = useState(false);
  const [competitionGroup, setCompetitionGroup] = useState("");
  const [openRoundModal, setOpenRoundModal] = useState(false);
  const [isRoundMedalClicked, setIsRoundMedalClicked] = useState(false);
  // on an event-day device (card 540f21a4): publish, unpublish and round
  // medals each follow the job
  const can = useCan();
  const canMedals = can("feis.roundmedals");
  const canMedalsRef = useRef(canMedals);
  canMedalsRef.current = canMedals;
  // the medals view only while the job still has round medals
  const showMedals = isRoundMedalClicked && canMedals;
  // round medals unticked meanwhile: close its window and its results view
  // (MC review 0699f116 R1-F6)
  useEffect(() => {
    if (canMedals) return;
    setOpenRoundModal(false);
    setIsRoundMedalClicked(false);
    setRoundMedalDetails([]);
  }, [canMedals]);
  const [roundMedalDetails, setRoundMedalDetails] = useState([]);
  const [selectedRJP, setSelectedRJP] = useState({
    round: 0,
    judge: "",
    places: "",
    selectedJudge: "",
    type: ""
  });
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const eventId = searchParams.get("id");
  const compId = searchParams.get("compId");

  useEffect(() => {
    getInitialDetails();
  }, []);

  useEffect(() => {
    const isSlotMode = adjudicatorCount === 9 || adjudicatorCount === 6;
    let scoreCount = 0;
    if (isSlotMode) {
      // Slot-mode response: each score entry is a single slot. Count only entries with a real score.
      results.forEach((result) =>
        (result.scores || []).forEach((s) => {
          if (s && s.slot_index != null && (s.score > 0 || s.position > 0)) scoreCount++;
        })
      );
    } else {
      // Legacy response: each score is a judge with scores_by_round[]
      results.forEach((result) =>
        (result.scores || []).forEach(
          (scoreObj) => (scoreCount += scoreObj?.scores_by_round?.length || 0)
        )
      );
    }
    const enabled = isSlotMode
      ? 9 * results.length === scoreCount
      : rounds * adjudicatorCount * results.length === scoreCount;
    setButtonEnabled(enabled);
  }, [adjudicatorCount, results, rounds]);

  const getInitialDetails = async () => {
    try {
      setInitialLoading(true);
      const eventUrl = `${process.env.REACT_APP_BASE_URL}/feis/${eventId}`;
      const eventResponse = await serviceConsumer("GET", eventUrl);
      setEvent(eventResponse.Feis);

      const currentCompUrl = `${process.env.REACT_APP_BASE_URL}/feis/competition/${compId}`;
      const currentCompResponse = await serviceConsumer("GET", currentCompUrl);
      setCompetitionDetails(currentCompResponse.competition);

      const resultsUrl = `${process.env.REACT_APP_BASE_URL}/feis/competition/scores/${currentCompResponse.competition._id}`;
      const resultsResponse = await serviceConsumer("GET", resultsUrl);
      setResults(resultsResponse.results);
      setInitialLoading(false);
    } catch (error) {
      navigate("/pageNotFound", { replace: true });
      setInitialLoading(false);
    }
  };

  const setCompetitionDetails = (selectedCompetition) => {
    setCompetitionGroup(selectedCompetition?.group);
    setCompetitionCode(selectedCompetition?.code);
    setCompetitionId(selectedCompetition?._id);
    setRounds(selectedCompetition?.rounds);
    setAdjudicatorCount(selectedCompetition?.adjudicators);
    setIsPublished(selectedCompetition?.results_published);
  };
  //print card handler
  const printHandler = async () => {
    const url = showMedals ? `${process.env.REACT_APP_BASE_URL}/feis/round/medals/print/${competitionId}?round=${selectedRJP?.round}&placements=${selectedRJP?.places}&judgeId=${selectedRJP?.judge}&type=${selectedRJP?.type}` : `${process.env.REACT_APP_BASE_URL}/feis/results/pdf/org/${competitionId}`;
    getPrintHandler(url, setLoading);
  };

  //publish or unpulish results
  const publishOrUnpublishHandler = async () => {
    try {
      setLoading(true);
      const publishUrl = `${process.env.REACT_APP_BASE_URL}/feis/competition/scores/publish/${competitionId}`;
      const unpublishUrl = `${process.env.REACT_APP_BASE_URL}/feis/competition/scores/unpublish/${competitionId}`;
      const url = isPublished ? unpublishUrl : publishUrl;
      const response = await serviceConsumer("POST", url);
      isPublished ? setIsPublished(false) : setIsPublished(true);
      setLoading(false);
      ShowToastMessage("success", response.message);
    } catch (error) {
      ShowToastMessage("error", error.messages);
      setLoading(false);
    }
  };

  const handleRoundMedal = async (details) => {
    if (!canMedals) return;
    try {
      setLoading(true);
      const { round, judge, places, selectedJudge, type } = details;
      const payload = {
        round: Number(round),
        placements: Number(places),
        judgeId: judge,
        type
      };
      const url = `${process.env.REACT_APP_BASE_URL}/feis/round/medals/${competitionId}`;
      const response = await serviceConsumer("POST", url, payload);
      // round medals unticked while this was pending (MC review 0699f116 R1-F6)
      if (!canMedalsRef.current) return;
      setRoundMedalDetails(response?.medals);
      setIsRoundMedalClicked(true);
      setSelectedRJP({ round, judge, selectedJudge, places, type });
    } catch (error) {
      ShowToastMessage("error", error.messages);
    } finally {
      setLoading(false);
      setOpenRoundModal(false);
    }
  };

  return (
    <>
      {loading && <Loader />}
      {initialLoading && <Loader />}
      <HeaderOrg />
      {!initialLoading && (
        <ManageTitle
          eventLogo={event?.logo}
          eventName={event?.name}
          eventType={event?.eventType}
          eventDate={event?.feis_date}
          onBackClick={() => {
            if(isRoundMedalClicked) {
                setIsRoundMedalClicked(false)
                setRoundMedalDetails([])
                setSelectedRJP({
                    round: 0,
                    judge: "",
                    places: "",
                    selectedJudge: "",
                })
            } else {
                window.history.back()
            }
          }}
          button={
            showMedals || !can(isPublished ? "feis.results.unpublish" : "feis.results.publish")
              ? ""
              : isPublished
              ? "Unpublish"
              : "Publish Results"
          }
          className={
            isPublished ? "outlined" : buttonEnabled ? "filled" : "disabled"
          }
          onClick={publishOrUnpublishHandler}
          textButton="Print"
          textBtnClass={buttonEnabled ? "text-btn" : "text-btn-disabled"}
          textBtnHandler={printHandler}
        />
      )}

      <div className="org-body-white">
        {!initialLoading && (
          <>
            <div className="org-body-title pt-4">{showMedals ? `Round ${selectedRJP?.round} - ${selectedRJP?.selectedJudge}` : "Complete results"}</div>
            <div className="mt-1 hr-org-body"></div>

            <div className="mt-4 d-md-flex align-items-start justify-content-between">
              {/* Left section */}
              <div
                className={`d-flex flex-wrap align-items-start ${
                  showMedals ? "gap-3 gap-md-4 w-100" : "gap-4"
                }`}
              >
                {/* Level */}
                <div
                  className={showMedals ? "flex-fill" : ""}
                  style={showMedals ? { minWidth: "200px" } : { minWidth: "300px" }}
                >
                  <div className="search-label">Level:</div>
                  <input
                    name="level"
                    className="check-in-input w-100"
                    value={competitionGroup}
                    readOnly
                  />
                </div>

                {/* Competition */}
                <div
                  className={showMedals ? "flex-fill" : ""}
                  style={showMedals ? { minWidth: "200px" } : { minWidth: "300px" }}
                >
                  <div className="search-label">Competition:</div>
                  <input
                    name="competition"
                    className="check-in-input w-100"
                    value={`${competitionCode}-${competitionGroup}`}
                    readOnly
                  />
                </div>

                {/* Show only if Round Medals clicked */}
                {showMedals && (
                  <>
                    <div className="flex-fill" style={{ minWidth: "200px" }}>
                      <div className="search-label">Round:</div>
                      <input
                        name="round"
                        className="check-in-input w-100"
                        value={selectedRJP?.round}
                        readOnly
                      />
                    </div>

                    <div className="flex-fill" style={{ minWidth: "200px" }}>
                      <div className="search-label">Adjudicator:</div>
                      <input
                        name="adjudicator"
                        className="check-in-input w-100"
                        value={selectedRJP?.selectedJudge}
                        readOnly
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Right section with button (only if Round Medals not clicked) */}
              {rounds > 1 && !showMedals && canMedals && (
                <div className="mt-3 mt-md-3 ms-md-4 flex-shrink-0">
                  <button
                    className="filled w-100 w-md-auto"
                    style={{ minWidth: "160px" }}
                    onClick={() => setOpenRoundModal(true)}
                  >
                    Round Medals
                  </button>
                </div>
              )}
            </div>

            {showMedals ? (
              <RoundMedalsTable
                results={roundMedalDetails}
                selectedRJP={selectedRJP}
              />
            ) : (
              <>
                {adjudicatorCount === 1 && (
                  <OneJudgeResult
                    rounds={rounds}
                    results={results}
                    keyProp={tableDataKey}
                    event={event}
                  />
                )}
                {adjudicatorCount === 3 && (
                  <ThreeJudgeResult
                    rounds={rounds}
                    results={results}
                    keyProp={tableDataKey}
                    event={event}
                  />
                )}
                {(adjudicatorCount === 9 || adjudicatorCount === 6) && (
                  <NineJudgeResult
                    results={results}
                    keyProp={tableDataKey}
                    event={event}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
      <ToastContainer />
      <Help />
      {openRoundModal && (
        <RoundMedalsModal
          open={openRoundModal}
          onClose={() => setOpenRoundModal(false)}
          rounds={rounds}
          competitionId={competitionId}
          handleRoundMedal={handleRoundMedal}
        />
      )}
    </>
  );
};

export default CompleteResults;
